// Renders a string that may contain Slack-style `<@USER_ID>` mention tokens
// by replacing each token with the corresponding team member's name (or
// "@unknown" if we can't resolve). Used by the notifications panel and
// elsewhere we surface notes / Slack-format text.
//
// Why we store `<@ID>` in the DB at all: workflow 01's note-mention trigger
// relies on the canonical Slack format to fire the in-app notification.
// Rendering them as raw tokens reaches the user is just a presentation
// gap — this component fixes that.

import { useMemo } from 'react';
import { useTeamMembers, type TeamMember } from '@/lib/queries';

const MENTION_RE = /<@([UWB][A-Z0-9]+)>/g;

interface Props {
  text: string | null | undefined;
  /** Optional: highlight @-mention chips. Default styling is just the
   *  resolved "@Name" inline; pass `chip` for a small accent pill. */
  variant?: 'inline' | 'chip';
  className?: string;
}

export function MentionText({ text, variant = 'inline', className }: Props) {
  const teamQ = useTeamMembers();
  const bySlackId = useMemo(() => {
    const m = new Map<string, TeamMember>();
    for (const t of teamQ.data ?? []) m.set(t.slack_user_id, t);
    return m;
  }, [teamQ.data]);

  if (!text) return null;

  const parts = useMemo(() => {
    const out: Array<{ kind: 'text' | 'mention'; value: string; name?: string }> = [];
    let last = 0;
    let m: RegExpExecArray | null;
    MENTION_RE.lastIndex = 0;
    while ((m = MENTION_RE.exec(text)) !== null) {
      if (m.index > last) {
        out.push({ kind: 'text', value: text.slice(last, m.index) });
      }
      const slackId = m[1];
      const name = bySlackId.get(slackId)?.name;
      out.push({ kind: 'mention', value: slackId, name });
      last = m.index + m[0].length;
    }
    if (last < text.length) out.push({ kind: 'text', value: text.slice(last) });
    return out;
  }, [text, bySlackId]);

  return (
    <span className={className}>
      {parts.map((p, i) => {
        if (p.kind === 'text') return <span key={i}>{p.value}</span>;
        const display = p.name ?? 'unknown';
        if (variant === 'chip') {
          return (
            <span
              key={i}
              className="inline-flex items-center rounded bg-tint-warning px-1 py-0 text-2xs font-medium text-tint-warning-fg"
            >
              @{display}
            </span>
          );
        }
        return (
          <span key={i} className="font-medium text-foreground/90">
            @{display}
          </span>
        );
      })}
    </span>
  );
}
